import type { MassSystem } from './MassSystem';

/**
 * Derives the blob's "threat aura" from current biomass. Living prey
 * within this aura flee regardless of line-of-sight — they can sense
 * the ooze nearby.
 *
 * At tier 1 (mass ~0.2) the aura is effectively zero — you're a blob
 * in a drain, nothing notices you. As biomass accumulates, the aura
 * grows and eventually covers the whole chamber, at which point
 * stealth/disguise/mimicry become the only way to close on prey that
 * flees on sight.
 *
 * Pure function of MassSystem — no state, no listeners. Re-query on
 * demand.
 */
export interface ThreatAuraOptions {
  /** Mass below which aura is zero (blob too small to register). */
  minMass?: number;
  /** Cap on aura radius in world units. */
  maxRadius?: number;
  /** Curve steepness — higher = aura grows faster per mass. */
  growthFactor?: number;
  /** Base offset at minMass (small but non-zero to start registering). */
  baseRadius?: number;
}

export class ThreatAuraSystem {
  private readonly minMass: number;
  private readonly maxRadius: number;
  private readonly growthFactor: number;
  private readonly baseRadius: number;

  constructor(
    private readonly mass: MassSystem,
    options: ThreatAuraOptions = {},
  ) {
    // Defaults tuned so the blob is invisible-to-the-ecosystem until it
    // has grown meaningfully (mass 1.5 is ~mid-tier-2). Before that
    // point nothing notices — pure exploration and static-food grazing.
    this.minMass = options.minMass ?? 1.5;
    this.maxRadius = options.maxRadius ?? 400;
    this.growthFactor = options.growthFactor ?? 25;
    this.baseRadius = options.baseRadius ?? 5;
  }

  get radius(): number {
    const m = this.mass.currentMass;
    if (m < this.minMass) return 0;
    const raw = this.baseRadius + Math.sqrt(m - this.minMass) * this.growthFactor;
    return Math.min(this.maxRadius, raw);
  }

  /** Normalized intensity 0..1 — 0 at minMass, 1 at maxRadius reached. */
  get intensity(): number {
    const r = this.radius;
    if (r === 0) return 0;
    return Math.min(1, r / this.maxRadius);
  }

  /**
   * Is the given target position inside the blob's aura right now?
   * Includes the target's own fear buffer — a skittish rat notices
   * the ooze from slightly farther than the radius implies.
   */
  isWithinAura(
    blobPos: { x: number; y: number },
    targetPos: { x: number; y: number },
    extraRange = 0,
  ): boolean {
    const r = this.radius + extraRange;
    if (r <= 0) return false;
    const dx = targetPos.x - blobPos.x;
    const dy = targetPos.y - blobPos.y;
    return dx * dx + dy * dy <= r * r;
  }
}
